// LAMMPS recipe selection for Atomic Mode.
//
// A "recipe" is a real LAMMPS potential setup (pair_style + potential file +
// units + atom_style) for a class of materials, so a run uses proper physics
// (EAM for metals, Vashishta for silica/water/SiC, AIREBO for carbon, ReaxFF
// for reactive organics) instead of the generic UFF Lennard-Jones fit.
//
// The recipe table is data-driven (packages/material-db/data/lammps-recipes.yaml)
// and only references potential files that exist in the Atomify bundle and pair
// styles its WASM build supports. Selection is by material id or exact element
// signature; anything unmatched returns null and the caller uses the universal
// UFF-LJ fallback.
//
// LICENSE NOTE: we never ship potential-file *data* — the recipe only names a
// file (e.g. "SiO2.vashishta"); the iframe bridge fetches it same-origin from
// the GPL atomify bundle. This module stays MIT/Apache.

import recipesYaml from "../../../../packages/material-db/data/lammps-recipes.yaml?raw";
import type { LammpsSystem } from "./lammps";
import { measurementVariableLines } from "./measurements";
import { parseYaml } from "./yaml";

export interface LammpsRecipe {
  id: string;
  match: { ids?: string[]; elements?: string[] };
  pairStyle: string;
  /** Potential file name. Empty for inline-LJ recipes (noble gases). */
  potential: string;
  /** Element symbols in atom-type order. */
  elements: string[];
  units: string;
  atomStyle: string;
  emitMasses: boolean;
  /** `funcfl` => `pair_coeff 1 1 <file>`; otherwise `* * <file> <elements>`. */
  pairCoeffForm: "funcfl" | "standard";
  /** Per-element formal charge (atom_style charge only). */
  charge?: Record<string, number>;
  /**
   * Inline Lennard-Jones parameters per element: symbol -> [epsilon, sigma].
   * When present (and no potential file), the script emits explicit
   * `pair_coeff i i eps sigma` lines instead of reading a potential file.
   */
  ljParams?: Record<string, [number, number]>;
  /**
   * Real crystal structure for geometrically-exact atom placement (fcc, bcc,
   * diamond, rocksalt, zincblende, wurtzite, hcp, graphene). When set, atoms
   * are built on this lattice at `lattice` Å instead of a generic cubic grid.
   */
  structure?: string;
  /** Lattice constant a (Å) for `structure`. */
  lattice?: number;
  /** Optional second lattice constant c (Å) for hexagonal structures. */
  latticeC?: number;
  /** Extra input-script lines inserted after pair_coeff. */
  extra: string[];
  /** Extra files (control/qeq) to fetch alongside the potential. */
  aux: string[];
}

type RawRecipe = Record<string, unknown>;

function toStrArray(v: unknown): string[] {
  return Array.isArray(v) ? v.map(String) : [];
}

function parseLjParams(
  v: unknown,
): Record<string, [number, number]> | undefined {
  if (!v || typeof v !== "object") return undefined;
  const out: Record<string, [number, number]> = {};
  for (const [sym, pair] of Object.entries(v as Record<string, unknown>)) {
    if (Array.isArray(pair) && pair.length >= 2) {
      out[sym] = [Number(pair[0]), Number(pair[1])];
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function parseRecipe(raw: RawRecipe): LammpsRecipe | null {
  const id = typeof raw.id === "string" ? raw.id : undefined;
  const pairStyle =
    typeof raw.pair_style === "string" ? raw.pair_style : undefined;
  const potential = typeof raw.potential === "string" ? raw.potential : "";
  const elements = toStrArray(raw.elements);
  const ljParams = parseLjParams(raw.lj_params);
  // A recipe needs a pair_style + elements, and EITHER a potential file OR
  // inline LJ params.
  if (!id || !pairStyle || elements.length === 0) return null;
  if (!potential && !ljParams) return null;
  const match = (raw.match ?? {}) as Record<string, unknown>;
  return {
    id,
    match: {
      ids: toStrArray(match.ids),
      elements: toStrArray(match.elements),
    },
    pairStyle,
    potential,
    elements,
    units: typeof raw.units === "string" ? raw.units : "metal",
    atomStyle: typeof raw.atom_style === "string" ? raw.atom_style : "atomic",
    emitMasses: raw.emit_masses !== false,
    pairCoeffForm: raw.pair_coeff_form === "funcfl" ? "funcfl" : "standard",
    charge:
      raw.charge && typeof raw.charge === "object"
        ? (raw.charge as Record<string, number>)
        : undefined,
    ljParams,
    structure: typeof raw.structure === "string" ? raw.structure : undefined,
    lattice: typeof raw.lattice === "number" ? raw.lattice : undefined,
    latticeC: typeof raw.lattice_c === "number" ? raw.lattice_c : undefined,
    extra: toStrArray(raw.extra),
    aux: toStrArray(raw.aux),
  };
}

// Parsed once.
export const RECIPES: LammpsRecipe[] = (() => {
  const doc = parseYaml(recipesYaml) as Record<string, unknown>;
  const list = Array.isArray(doc.recipes) ? (doc.recipes as RawRecipe[]) : [];
  return list.map(parseRecipe).filter((r): r is LammpsRecipe => r !== null);
})();

/** Set-equality on element symbols (order-insensitive). */
function sameElementSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sb = new Set(b);
  return a.every((x) => sb.has(x));
}

/**
 * Pick the best recipe for a selection, given the set of material ids present
 * and the set of distinct composition elements. Matches by material id first
 * (most specific), then by exact element signature. Returns null when nothing
 * matches — the caller then uses the UFF-LJ fallback.
 *
 * To keep a run coherent, a recipe is only chosen when the selection is
 * "dominated" by a single matchable material/element-set: if the selection
 * mixes elements a recipe can't represent, we fall back.
 */
export function selectRecipe(
  materialIds: string[],
  elementSymbols: string[],
): LammpsRecipe | null {
  // id match: every present material id is covered by the same recipe.
  for (const recipe of RECIPES) {
    const ids = recipe.match.ids ?? [];
    if (ids.length === 0) continue;
    if (materialIds.every((id) => ids.includes(id))) return recipe;
  }
  // element-signature match: the selection's element set equals the recipe's.
  for (const recipe of RECIPES) {
    const els = recipe.match.elements ?? [];
    if (els.length === 0) continue;
    if (sameElementSet(elementSymbols, els)) return recipe;
  }
  return null;
}

export interface RecipeScript {
  inputScript: string;
  /** Potential/aux file names the bridge must fetch from the bundle. */
  potentialFiles: string[];
  /** Element -> 1-based atom type, matching the recipe's element order. */
  typeByElement: Map<string, number>;
}

/**
 * Render the LAMMPS input script for a recipe. The data file (geometry) is
 * produced separately; this wires up units, the real potential, and the run.
 * `masses` maps element symbol -> atomic weight for the `mass` lines.
 */
export function writeRecipeScript(
  recipe: LammpsRecipe,
  dataFileName: string,
  masses: Map<string, number>,
  meanTempK: number,
): RecipeScript {
  const typeByElement = new Map<string, number>();
  recipe.elements.forEach((sym, i) => typeByElement.set(sym, i + 1));

  const L: string[] = [];
  L.push("# Atomify hotspot run — generated by RealChem Atomic Mode");
  L.push(`# Recipe: ${recipe.id} (${recipe.pairStyle})`);
  for (const [sym, type] of typeByElement) {
    L.push(`#/atom ${type} ${sym}`);
  }
  L.push("");
  L.push(`units ${recipe.units}`);
  L.push("dimension 3");
  L.push("boundary p p p");
  L.push(`atom_style ${recipe.atomStyle}`);
  L.push("");
  L.push(`read_data ${dataFileName}`);
  L.push("");
  L.push(`pair_style ${recipe.pairStyle}`);
  if (recipe.ljParams) {
    // Inline Lennard-Jones: explicit `pair_coeff i i eps sigma` per element.
    for (const sym of recipe.elements) {
      const p = recipe.ljParams[sym];
      const type = typeByElement.get(sym);
      if (p && type) L.push(`pair_coeff ${type} ${type} ${p[0]} ${p[1]}`);
    }
  } else if (recipe.pairCoeffForm === "funcfl") {
    // funcfl: one element, `pair_coeff 1 1 <file>` (mass from the file).
    L.push(`pair_coeff 1 1 ${recipe.potential}`);
  } else {
    L.push(
      `pair_coeff * * ${recipe.potential} ${recipe.elements.join(" ")}`.trim(),
    );
  }
  if (recipe.emitMasses) {
    for (const sym of recipe.elements) {
      const m = masses.get(sym);
      if (m) L.push(`mass ${typeByElement.get(sym)} ${m.toFixed(4)}`);
    }
  }
  L.push("");
  for (const line of recipe.extra) L.push(line);
  if (recipe.extra.length > 0) L.push("");
  L.push("neigh_modify every 1 delay 0 check yes");
  L.push("");
  const temp = Math.max(1, meanTempK).toFixed(2);
  L.push(`velocity all create ${temp} 4928459 mom yes rot yes dist gaussian`);
  L.push("fix 1 all nve");
  L.push(
    `fix 2 all langevin ${temp} ${temp} ${recipe.units === "real" ? "100.0" : "0.1"} 904297`,
  );
  L.push("");

  // ---- Measurements ----
  // Real, extractable observables so a run produces science, not just a movie:
  //   • mean-squared displacement → self-diffusion coefficient D = MSD/(6t)
  //   • radial distribution function g(r) → structure / melting signature
  //   • thermo log of T, PE, vol → energy/temperature for phase behaviour
  // These are emitted as #/-free LAMMPS computes; atomify plots thermo live and
  // the data is available in the in-browser Jupyter for analysis.
  L.push("# --- measurements (diffusion, structure, thermodynamics) ---");
  L.push("compute msd all msd");
  L.push("compute rdf all rdf 100");
  L.push("fix msdlog all ave/time 10 1 10 c_msd[4] file msd.txt mode scalar");
  L.push(
    "fix rdflog all ave/time 100 1 1000 c_rdf[*] file rdf.txt mode vector",
  );
  L.push("thermo_style custom step temp pe ke etotal press vol c_msd[4]");
  for (const line of measurementVariableLines()) L.push(line);
  L.push("");
  L.push(`timestep ${recipe.units === "real" ? "0.5" : "0.001"}`);
  L.push("thermo 50");
  L.push("run 5000");
  L.push("");

  return {
    inputScript: L.join("\n"),
    // Inline-LJ recipes have no potential file to fetch.
    potentialFiles: [
      ...(recipe.potential ? [recipe.potential] : []),
      ...recipe.aux,
    ],
    typeByElement,
  };
}

/**
 * Render a hybrid LAMMPS script for a mixed selection containing carbon: AIREBO
 * governs the carbon (so it bonds into real sp² graphene) and lj/cut (UFF)
 * governs every other element plus all cross interactions. `carbonType` is the
 * 1-based atom type of carbon. Geometric mixing is applied explicitly per pair
 * (hybrid does not auto-mix across sub-styles).
 */
export function writeHybridCarbonScript(
  system: LammpsSystem & { carbonType: number },
  dataFileName: string,
): { inputScript: string; potentialFiles: string[] } {
  const L: string[] = [];
  L.push("# Atomify hotspot run — generated by RealChem Atomic Mode");
  L.push(
    "# Hybrid: AIREBO carbon (real graphene) + Lennard-Jones for the rest.",
  );
  for (const t of system.types) L.push(`#/atom ${t.id} ${t.symbol}`);
  L.push("");
  L.push("units metal");
  L.push("dimension 3");
  L.push("boundary p p p");
  L.push("atom_style atomic");
  L.push("");
  L.push(`read_data ${dataFileName}`);
  L.push("");

  // Largest LJ cutoff among non-carbon types.
  const maxSigma = system.types.reduce((m, t) => Math.max(m, t.sigma), 0);
  const cut = Math.max(10, 3 * maxSigma).toFixed(2);
  L.push(`pair_style hybrid airebo 3.0 1 1 lj/cut ${cut}`);
  // AIREBO for carbon: element tail is C for the carbon type, NULL otherwise.
  const tail = system.types
    .map((t) => (t.id === system.carbonType ? "C" : "NULL"))
    .join(" ");
  L.push(`pair_coeff * * airebo CH.airebo ${tail}`);
  // lj/cut for every pair NOT both-carbon (geometric mixing, eV·Å in metal).
  for (let i = 0; i < system.types.length; i++) {
    for (let j = i; j < system.types.length; j++) {
      const a = system.types[i];
      const b = system.types[j];
      if (a.id === system.carbonType && b.id === system.carbonType) continue;
      const eps = Math.sqrt(a.epsilon * b.epsilon);
      const sigma = Math.sqrt(a.sigma * b.sigma);
      L.push(
        `pair_coeff ${a.id} ${b.id} lj/cut ${eps.toFixed(5)} ${sigma.toFixed(4)}`,
      );
    }
  }
  for (const t of system.types) {
    L.push(`mass ${t.id} ${t.mass.toFixed(4)} # ${t.symbol}`);
  }
  L.push("");
  L.push("neighbor 2.0 bin");
  L.push("neigh_modify every 1 delay 0 check yes");
  L.push("");
  // Relax close contacts (the LJ cluster sits just above the sheet).
  L.push("minimize 1.0e-4 1.0e-6 1000 10000");
  L.push("");
  const temp = Math.max(1, system.meanTempK).toFixed(2);
  L.push(`velocity all create ${temp} 4928459 mom yes rot yes dist gaussian`);
  L.push("fix 1 all nve");
  L.push(`fix 2 all langevin ${temp} ${temp} 0.1 904297`);
  L.push("");
  L.push("compute msd all msd");
  L.push("thermo_style custom step temp pe ke etotal press vol c_msd[4]");
  for (const line of measurementVariableLines()) L.push(line);
  L.push("timestep 0.001");
  L.push("thermo 50");
  L.push("run 5000");
  L.push("");
  return { inputScript: L.join("\n"), potentialFiles: ["CH.airebo"] };
}

// ReaxFF reactive force fields available in the bundle, with the elements each
// covers. A mixed selection whose elements are ALL within one field can be run
// fully reactively (bonds form/break across the material boundary). These are
// the real Sandia/van-Duin-group force fields shipped with LAMMPS (each carries
// its own DATE/CONTRIBUTOR/CITATION header); we only name them — the iframe
// bridge fetches the data same-origin from the GPL atomify bundle.
//
// `label` describes the chemistry the field was parameterised for (shown in the
// run log so the user knows WHICH reactive model they're getting). The `X`
// placeholder atom some fields define is intentionally omitted: we never map an
// atom type to it.
export interface ReaxffField {
  potential: string;
  elements: string[];
  /** Files fetched alongside the field (the reaxff control file). */
  aux: string[];
  /** Human-readable description of the parameterised chemistry. */
  label: string;
}
// Ordered so that, on ties, the earliest-declared (most general-purpose) field
// wins; selection otherwise prefers the field with the FEWEST elements that
// still covers the selection (tightest fit = best-parameterised chemistry).
export const REAXFF_FIELDS: ReaxffField[] = [
  {
    potential: "ffield.reax.cho",
    elements: ["C", "H", "O"],
    aux: ["lmp_control"],
    label: "C/H/O combustion & organics (Chenoweth–van Duin–Goddard 2008)",
  },
  {
    potential: "ffield.reax.ZnOH",
    elements: ["H", "O", "Zn"],
    aux: ["lmp_control"],
    label: "Zn/O/H — zinc oxide & water interfaces (Raymand et al. 2010)",
  },
  {
    potential: "ffield.reax.rdx",
    elements: ["C", "H", "O", "N"],
    aux: ["lmp_control"],
    label: "C/H/O/N energetic materials & nitrogen chemistry (Strachan et al.)",
  },
  {
    potential: "ffield.reax.Fe_O_C_H",
    elements: ["C", "H", "O", "Fe"],
    aux: ["lmp_control"],
    label: "Fe/O/C/H — iron oxidation (rust) & organics (Aryanpour et al.)",
  },
  {
    potential: "ffield.reax.V_O_C_H",
    elements: ["C", "H", "O", "V"],
    aux: ["lmp_control"],
    label: "V/O/C/H — vanadium oxide catalysis (Chenoweth et al.)",
  },
  {
    potential: "ffield.reax.mattsson",
    elements: ["C", "H", "O", "N", "S"],
    aux: ["lmp_control"],
    label: "C/H/O/N/S hydrocarbons & polymers (Mattsson et al.)",
  },
  {
    potential: "ffield.reax.FC",
    elements: ["C", "H", "O", "N", "S", "F", "Pt", "Cl", "Ni"],
    aux: ["lmp_control"],
    label: "C/H/O/N/S/F/Pt/Cl/Ni — fluorocarbons & catalysis (Singh 2013)",
  },
];

/**
 * If every element in the selection is covered by a ReaxFF field, return the
 * TIGHTEST-fitting field (fewest elements that still cover the whole selection
 * — its parameters are specialised for that chemistry) plus the present
 * elements in the field's own order. The caller can then run the whole mix
 * reactively. Returns null when no single field covers every element.
 */
export function selectReactiveField(
  elementSymbols: string[],
): { field: ReaxffField; elements: string[] } | null {
  if (elementSymbols.length === 0) return null;
  let best: ReaxffField | null = null;
  for (const field of REAXFF_FIELDS) {
    const set = new Set(field.elements);
    if (!elementSymbols.every((e) => set.has(e))) continue;
    // Prefer fewer elements (tighter fit); first declared wins ties.
    if (!best || field.elements.length < best.elements.length) best = field;
  }
  if (!best) return null;
  const elements = best.elements.filter((e) => elementSymbols.includes(e));
  return { field: best, elements };
}

/**
 * Render a fully-reactive ReaxFF script for a mixed selection whose elements
 * fit one ReaxFF field. Unlike the hybrid (which only lets materials touch via
 * van der Waals), ReaxFF spans all elements, so bonds can form and break ACROSS
 * the material boundary — real interfacial chemistry (combustion, oxidation…).
 * `masses` maps element symbol -> atomic weight.
 */
export function writeReactiveScript(
  field: ReaxffField,
  elements: string[],
  dataFileName: string,
  masses: Map<string, number>,
  meanTempK: number,
): { inputScript: string; potentialFiles: string[] } {
  const L: string[] = [];
  L.push("# Atomify hotspot run — generated by RealChem Atomic Mode");
  L.push("# Reactive ReaxFF run: bonds may form/break across materials.");
  L.push(`# Field: ${field.potential} — ${field.label}`);
  elements.forEach((sym, i) => L.push(`#/atom ${i + 1} ${sym}`));
  L.push("");
  L.push("units real");
  L.push("dimension 3");
  L.push("boundary p p p");
  L.push("atom_style charge");
  L.push("");
  L.push(`read_data ${dataFileName}`);
  L.push("");
  L.push("pair_style reaxff lmp_control");
  L.push(`pair_coeff * * ${field.potential} ${elements.join(" ")}`);
  elements.forEach((sym, i) => {
    const m = masses.get(sym);
    if (m) L.push(`mass ${i + 1} ${m.toFixed(4)}`);
  });
  // Read per-element electronegativity/hardness straight from the ReaxFF field
  // (the `reaxff` keyword) rather than a separate param.qeq: this matches the
  // pair_coeff element mapping exactly, so it's correct for ANY field and for
  // any SUBSET of a field's elements (a fixed param.qeq, indexed by atom type,
  // would mis-assign params when only some of a field's elements are present).
  L.push("fix reaxqeq all qeq/reax 1 0.0 10.0 1e-6 reaxff");
  L.push("");
  L.push("neighbor 2.0 bin");
  L.push("neigh_modify every 1 delay 0 check yes");
  L.push("");
  const temp = Math.max(1, meanTempK).toFixed(2);
  L.push(`velocity all create ${temp} 4928459 mom yes rot yes dist gaussian`);
  L.push("fix 1 all nve");
  // Gentler damping (fs) lets reaction heat dissipate without freezing kinetics.
  L.push(`fix 2 all langevin ${temp} ${temp} 100.0 904297`);
  L.push("");
  L.push("compute msd all msd");
  L.push("thermo_style custom step temp pe ke etotal press vol c_msd[4]");
  for (const line of measurementVariableLines()) L.push(line);
  L.push("timestep 0.25");
  L.push("thermo 50");
  L.push("run 5000");
  L.push("");
  return {
    inputScript: L.join("\n"),
    potentialFiles: [field.potential, ...field.aux],
  };
}
